import { clipSeed, vary, cutAt, isTransition, TRANSITION_STYLES, type Clip } from "@hudhud/core";
import { FPS, HEIGHT, WIDTH, msToFrame } from "../types.js";
import { interpolate, clamp01 } from "./interpolate.js";
export { clamp01 };

export type CutStyle = Clip["cutStyle"];
export { isTransition, TRANSITION_STYLES };

/** Frames of overlap at one cut (the one after piece `cutIndex`; the clip's own style when none is given): 0 for hard / punch. */
export function transitionFrames(clip: Clip, cutIndex?: number): number {
  const cut = cutIndex == null ? { style: clip.cutStyle, ms: clip.transitionMs } : cutAt(clip, cutIndex);
  if (!isTransition(cut.style)) return 0;
  const ms = cut.ms * (0.85 + 0.3 * vary(clipSeed(clip), 3)); // no two clips dissolve at exactly the same speed
  return Math.max(2, Math.min(msToFrame(ms), Math.round(FPS * 1.2)));
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/** A piece's state through a transition, as numbers: only the fields the style animates are present, so "nothing" stays `{}`. */
export interface MotionValues { opacity?: number; tx?: number; ty?: number; scale?: number; blur?: number }
/** The same as CSS, for the React renderers. */
export interface MotionCss { opacity?: number; transform?: string; filter?: string }
export interface PieceMotionValues { motion: MotionValues; overlayOpacity: number; overlayColor: string }
export interface PieceMotion { style: MotionCss; overlayOpacity: number; overlayColor: string }

/** CSS for a motion: one transform function per axis, in translate → scale order, and a blur when there is one. */
export function motionCss(m: MotionValues): MotionCss {
  const css: MotionCss = {};
  if (m.opacity !== undefined) css.opacity = m.opacity;
  const t: string[] = [];
  if (m.tx !== undefined) t.push(`translateX(${m.tx}px)`);
  if (m.ty !== undefined) t.push(`translateY(${m.ty}px)`);
  if (m.scale !== undefined) t.push(`scale(${m.scale})`);
  if (t.length) css.transform = t.join(" ");
  if (m.blur !== undefined) css.filter = `blur(${m.blur}px)`;
  return css;
}

/** Visual state of the INCOMING piece `t` (0..1) through a transition. The outgoing piece keeps playing underneath for the overlap. */
export function incomingMotionValues(style: CutStyle, t: number, pieceIndex: number, lite = false): PieceMotionValues {
  const k = Math.max(0, Math.min(1, t));
  const none: PieceMotionValues = { motion: {}, overlayOpacity: 0, overlayColor: "#fff" };
  if (k >= 1) return none;
  const dir = pieceIndex % 2 === 0 ? 1 : -1;
  switch (style) {
    case "dissolve": return { ...none, motion: { opacity: easeInOut(k) } };
    case "whip": {
      const e = easeOut(k);
      return { ...none, motion: { tx: dir * (1 - e) * WIDTH, ...(lite ? {} : { blur: (1 - e) * 22 }) } };
    }
    case "zoom": {
      const e = easeOut(k);
      return { ...none, motion: { opacity: Math.min(1, k * 2), scale: 1.35 - 0.35 * e, ...(lite ? {} : { blur: (1 - e) * 10 }) } };
    }
    case "flash": {
      const white = k < 0.5 ? k * 2 : 1 - (k - 0.5) * 2;
      return { motion: { opacity: k < 0.5 ? 0 : 1 }, overlayOpacity: Math.pow(white, 0.7), overlayColor: "#fff" };
    }
    case "slide": {
      const e = easeInOut(k);
      return { ...none, motion: { ty: dir * (1 - e) * HEIGHT } };
    }
    case "blur": {
      const e = easeInOut(k);
      return { ...none, motion: { opacity: e, ...(lite ? {} : { blur: (1 - e) * 30 }) } };
    }
    default: return none;
  }
}
export function incomingMotion(style: CutStyle, t: number, pieceIndex: number, lite = false): PieceMotion {
  const v = incomingMotionValues(style, t, pieceIndex, lite);
  return { style: motionCss(v.motion), overlayOpacity: v.overlayOpacity, overlayColor: v.overlayColor };
}

/** What the OUTGOING piece does while the next one arrives: whip/slide push it off, zoom shrinks it, the rest hold. */
export function outgoingMotionValues(style: CutStyle, t: number, nextIndex: number, lite = false): MotionValues {
  const k = Math.max(0, Math.min(1, t));
  if (k <= 0) return {};
  const dir = nextIndex % 2 === 0 ? 1 : -1;
  switch (style) {
    case "whip": return { tx: -dir * easeOut(k) * WIDTH, ...(lite ? {} : { blur: easeOut(k) * 22 }) };
    case "slide": return { ty: -dir * easeInOut(k) * HEIGHT };
    case "zoom": return { scale: 1 - 0.15 * easeOut(k), opacity: 1 - k };
    default: return {};
  }
}
export const outgoingMotion = (style: CutStyle, t: number, nextIndex: number, lite = false): MotionCss => motionCss(outgoingMotionValues(style, t, nextIndex, lite));

/** Per-cut punctuation: shake offsets and the strength (0..1) of a flash / glitch, `f` frames since the cut. */
export function cutShake(f: number, on: boolean): { x: number; y: number; rot: number } {
  if (!on || f < 0 || f > 10) return { x: 0, y: 0, rot: 0 };
  const decay = 1 - f / 10;
  const amp = 26 * decay;
  return { x: Math.sin(f * 2.9) * amp, y: Math.cos(f * 2.1) * amp * 0.6, rot: Math.sin(f * 1.7) * 0.8 * decay };
}
export const cutFlash = (f: number, on: boolean) => (!on || f < 0 || f > 6 ? 0 : 0.9 * (1 - f / 6));
export const cutGlitch = (f: number, on: boolean) => (!on || f < 0 || f > 5 ? 0 : 1 - f / 5);

/** Old-projector flicker: brightness multiplier around 1. */
export function flickerAt(frame: number, on: boolean): number {
  if (!on) return 1;
  const a = Math.sin(frame * 0.9) * 0.025 + Math.sin(frame * 3.7) * 0.02 + Math.sin(frame * 11.3) * 0.012;
  return 1 + a;
}

/** Where the light leak sits: a slow drift across the frame over ~8 s. */
export function leakPosition(frame: number): { x: number; y: number; opacity: number } {
  const t = frame / (FPS * 8);
  const x = 0.5 + 0.55 * Math.sin(t * Math.PI * 2);
  const y = 0.3 + 0.35 * Math.cos(t * Math.PI * 2 * 0.7);
  const opacity = 0.35 + 0.15 * Math.sin(t * Math.PI * 2 * 1.3);
  return { x, y, opacity };
}

/** Deterministic sparkles: `n` gold particles drifting up, each with its own phase; positions in 0..1 of the canvas. */
export function sparkles(frame: number, n = 26): { x: number; y: number; r: number; a: number }[] {
  const out: { x: number; y: number; r: number; a: number }[] = [];
  for (let i = 0; i < n; i++) {
    const seed = (i * 9301 + 49297) % 233280 / 233280;
    const seed2 = (i * 7919 + 104729) % 233280 / 233280;
    const speed = 0.0018 + seed * 0.002;
    const y = 1.05 - ((frame * speed + seed2) % 1.15);
    const x = seed2 + Math.sin((frame + i * 17) * 0.02) * 0.03;
    const twinkle = 0.5 + 0.5 * Math.sin(frame * (0.15 + seed * 0.2) + i);
    out.push({ x, y, r: 3 + seed * 5, a: 0.35 + 0.65 * twinkle });
  }
  return out;
}

export const lerp = interpolate;

/** The hand-overs at either end of the video, as frames of overlap: the opener into the video, the video into the end card (0 = no transition there). */
export function boundaryFrames(clip: Clip, which: "opener" | "end"): number {
  if (which === "opener" && !(clip.hook.enabled && clip.hook.mode === "intro" && ["blur", "card", "split", "type"].includes(clip.hook.style))) return 0;
  if (which === "end" && !clip.endCard.enabled) return 0;
  return transitionFrames(clip, which === "opener" ? -1 : clip.segments.length - 1);
}

/** Frames a piece's voice eases in and out over at its edges: a hard join clicks and two voices must never overlap through a transition. */
export const VOICE_EDGE_FRAMES = 2;
/**
 * The voice's level `f` frames into a piece of `pieceFrames` frames (the piece's own length, without any transition tail): eased in
 * over the first frames, eased out into the piece's out-point, and silent after it, so the next piece's words start alone and on time.
 */
export function voiceEdgeGain(f: number, pieceFrames: number, edge = VOICE_EDGE_FRAMES): number {
  if (f < 0 || f >= pieceFrames) return 0;
  const e = Math.max(1, Math.min(edge, Math.floor(pieceFrames / 2)));
  return Math.max(0, Math.min(1, (f + 1) / (e + 1), (pieceFrames - f) / (e + 1)));
}
